import { HttpClient, HttpParams } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { UserData, UserList } from './user-model';
import { Group } from '../groups/group-model';
import {
  baseUrl,
  ENDPOINTS,
} from '../../../../common/constants/endpoints.constants';
import {
  ApiResponse,
  Meta,
} from '../../../../common/models/api-response.model';
import { RoleTypes } from '../../../../common/constants/role-types.constant';
import { Status } from '../../../../common/enums/user-status';
import { SearchEnum } from '../../../../common/enums/search.enum';
import {
  FEATURE_FLAGS,
  FeatureFlagsService,
} from '../../../employee-flow/my-inbox/contributed-tasks/contributed-tasks.imports';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  http = inject(HttpClient);
  prefix = baseUrl;
  readonly featureFlagsService = inject(FeatureFlagsService);

  getLegalEntities() {
    return this.http.get<{ data: any }>(`${this.prefix}/lookup/legal-entities`);
  }

  addUser(data: any) {
    return this.http.post(`${this.prefix}/dashboard/users`, data);
  }

  updateUser(data: any, id: string) {
    return this.http.put(`${this.prefix}/dashboard/users/${id}`, data);
  }

  getUsersList(
    page: number,
    per_page: number,
    filter: string,
    sort?: { key: string; value: 'asc' | 'desc' },
    search?: string
  ) {
    let params = new HttpParams()
      .append('page', page)
      .append('per_page', per_page);

    let isEmployeeVendorPortalEnabled = signal<boolean>(
      this.featureFlagsService.getFeatureFlagValue(
        FEATURE_FLAGS.SYSTEM.portal_settings
      )
    );

    if (!isEmployeeVendorPortalEnabled()) {
      params = params.append('filters[not_role]', RoleTypes.VENDOR);
    }

    if (sort?.key) {
      params = params.append(`sort[${sort.key}]`, sort.value);
    }

    if (filter && filter !== 'all') {
      if (filter === 'activated_before') {
        const numberOfDays = 14;
        params = params
          .append('filters[state]', 'active')
          .append('filters[activated_before]', numberOfDays);
      } else {
        params = params.append('filters[state]', filter);
      }
    }

    if (search) {
      params = params.append(SearchEnum.keyword, search);
    }

    return this.http.get<ApiResponse<UserList>>(
      `${this.prefix}/dashboard/users`,
      { params }
    );
  }

  getInsights() {
    const params = new HttpParams().append('filters[not_role]', 'vendor');
    return this.http.get(`${this.prefix}/dashboard/users/insights`, { params });
  }

  userLookup(data: any) {
    const filterMappings = {
      name: 'filters[search]',
      page: 'page',
      perPage: 'per_page',
      filtersRole: 'filters[role]',
      filtersNotRole: 'filters[not_role]',
      filtersState: 'filters[state]',
      filtersNotId: 'filters[not_id]',
      filtersNotGroup: 'filters[not_group][id]',
      filtersNotState: 'filters[not_states][0]',
      filtersNotStateCancelled: 'filters[not_states][1]',
      filtersNotPermissionForResource:
        'filters[not_permission_for_resource][name]',
      filtersNotPermissionForResourceResourceType:
        'filters[not_permission_for_resource][resource_type]',
      filtersNotPermissionForResourceResourceId:
        'filters[not_permission_for_resource][resource_id]',

      filtersNotPermissionForResourcesResourceType0:
        'filters[not_permissions_for_resources][0][resource_type]',
      filtersNotPermissionForResourcesResourceId0:
        'filters[not_permissions_for_resources][0][resource_id]',
      filtersNotPermissionForResourcesResourceType1:
        'filters[not_permissions_for_resources][1][resource_type]',
      filtersNotPermissionForResourcesResourceId1:
        'filters[not_permissions_for_resources][1][resource_id]',
      filtersNotCollaboratedForFlow: 'filters[not_collaborated_for_flow]',
    };

    let params = new HttpParams();

    Object.entries(filterMappings).forEach(([key, paramName]) => {
      if (data[key]) {
        params = params.append(paramName, data[key]);
      }
    });

    params = params.append('filters[not_states][2]', Status.DELETED);

    return this.http.get<{ data: any; meta: Meta }>(
      `${ENDPOINTS.Lookup.User}`,
      { params }
    );
  }

  getUserDetails(userId: string) {
    return this.http.get<{ data: UserData }>(
      `${this.prefix}/dashboard/users/${userId}`
    );
  }

  reInviteUser(userId: string) {
    return this.http.post(
      `${this.prefix}/dashboard/users/${userId}/re-invite`,
      {}
    );
  }

  cancelInvitation(userId: string, replacement_user_id: string) {
    const payload: { replacement_user_id?: string } = { replacement_user_id };

    if (!replacement_user_id) delete payload.replacement_user_id;

    return this.http.post(
      ENDPOINTS.Admin.Users.CancelInvitation.replace('{userId}', userId),
      payload
    );
  }

  deleteUser(userId: string, replacementUserId?: string) {
    const options = replacementUserId
      ? { body: { replacement_user_id: replacementUserId } }
      : {};

    return this.http.delete(
      ENDPOINTS.Admin.Users.Delete.replace('{userId}', userId),
      options
    );
  }

  deactivateUser(userId: string, replacementUserId?: string) {
    const body =
      replacementUserId === undefined
        ? {}
        : { replacement_user_id: replacementUserId };

    return this.http.put(
      `${this.prefix}/dashboard/users/${userId}/deactivate`,
      body
    );
  }

  activateUser(userId: string) {
    return this.http.put(
      `${this.prefix}/dashboard/users/${userId}/activate`,
      {}
    );
  }

  grantEmployeeAccess(userId: string) {
    return this.http.put(
      ENDPOINTS.Admin.Users.GrantEmployeeAccess.replace('{userId}', userId),
      {}
    );
  }

  removeEmployeeAccess(userId: string, replacementUserId: string) {
    return this.http.put(
      ENDPOINTS.Admin.Users.RemoveEmployeeAccess.replace('{userId}', userId),
      { replacement_user_id: replacementUserId }
    );
  }

  resetPassword(userId: string) {
    return this.http.post(
      `${this.prefix}/dashboard/users/reset-password/${userId}/request`,
      {}
    );
  }

  removeUserRole(role_name: string, userId: string) {
    return this.http.patch(
      `${this.prefix}/dashboard/users/${userId}/change-role`,
      { role_name }
    );
  }

  assignUserRole(data: any) {
    return this.http.patch(`${this.prefix}/dashboard/users/assign-role`, data);
  }

  removeRoleFromUser(userId: string, role_name: string) {
    return this.http.patch(
      `${this.prefix}/dashboard/users/${userId}/de-assign-role`,
      { role_name: role_name }
    );
  }

  getUserByRoleList(page: number, perPage: number, role: string) {
    const params = new HttpParams()
      .append('sort[name][type]', 'asc')
      .append('page', page)
      .append('per_page', perPage)
      .append('filters[role]', role);
    return this.http.get(`${this.prefix}/dashboard/users`, { params });
  }

  getUserGroups(
    userId: string,
    page: number,
    per_page: number,
    searchName = ''
  ) {
    const params = new HttpParams()
      .append('page', page)
      .append('per_page', per_page)
      .append('filters[search]', searchName);
    return this.http.get<ApiResponse<Group>>(
      `${this.prefix}/dashboard/users/${userId}/groups`,
      { params }
    );
  }
}
